import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import SpeedDialAction from '@mui/material/SpeedDialAction';
import Typography from '@mui/material/Typography';
import type { SpeedDialActionProps, Theme } from '@mui/material';

import { EMPTY_OBJECT, NULL_FUNCTION } from '@e-socna/empty';

import { MessageList } from '../MessageList';

import type { SpeedDialAction as CustomSpeedDialAction } from './types/action.type';

interface Props extends SpeedDialActionProps {
  readonly action: CustomSpeedDialAction;
}

export const disabledProps = {
  sx: {
    color: (theme: Readonly<Theme>) => theme.palette.grey[300],
    cursor: 'not-allowed',
  },
  onClick: NULL_FUNCTION,
} as const;

const titleOf = ({ name, gate }: CustomSpeedDialAction) =>
  gate && !gate.isAllowed && gate.reasons.length ? (
    <>
      <Typography sx={{ fontWeight: 'bold' }} variant="inherit">
        {name}
      </Typography>
      <MessageList messages={gate.reasons} />
    </>
  ) : (
    name
  );

export const Action = ({ action, ...muiProps }: Readonly<Props>) => {
  const navigate = useNavigate();
  const handleActionClick = useCallback(() => {
    if (action.to) {
      void navigate(action.to);
    }
    if (action.onClick) {
      action.onClick();
    }
  }, [action, navigate]);

  return (
    <SpeedDialAction
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...muiProps}
      icon={action.icon}
      key={action.id}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(action.isDisabled || (action.gate && !action.gate.isAllowed)
        ? disabledProps
        : {
            onClick: handleActionClick,
          })}
      slotProps={{
        ...muiProps.slotProps,
        // A tooltip whose title is not a text only names its button while
        // shown: the action keeps its name meanwhile.
        fab: {
          ...(muiProps.slotProps?.fab ?? EMPTY_OBJECT),
          'aria-label': action.name,
        },
        tooltip: {
          ...(muiProps.slotProps?.tooltip ?? EMPTY_OBJECT),
          title: titleOf(action),
        },
      }}
    />
  );
};
